import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import VerifiedTierBadge from "../components/VerifiedTierBadge";
import TrustScoreBadge from "../components/TrustScoreBadge";
import AIMatchSearch from "../components/AIMatchSearch";
import ElderBadge from "../components/ElderBadge";
import VerifiedBadge from "../components/VerifiedBadge";
import StarRating from "../components/StarRating";
import { hideOnError } from "../lib/imgFallback";
import { Search, Filter, Sparkles, X, ArrowUpDown } from "lucide-react";
import { displayName } from "../lib/displayName";

const TIER_FILTERS = [
  { id: "", label: "Any" },
  { id: "practitioner", label: "Practitioner+" },
  { id: "temple", label: "Temple+" },
  { id: "initiate", label: "Initiate+" },
  { id: "elder", label: "Elder only" },
];

const SORTS = [
  { id: "trust", label: "Trust Score · highest first" },
  { id: "tier", label: "Tier · highest first" },
  { id: "endorsements", label: "Most endorsed" },
  { id: "reviews", label: "Most reviewed" },
  { id: "rating", label: "Highest rated" },
  { id: "newest", label: "Newest first" },
];

const CATEGORIES = ["Readings", "Love Work", "Prosperity", "Protection", "Classes", "Initiations"];

export default function Discover() {
  const [filters, setFilters] = useState({
    tier: "", tradition: "", minEndorsements: 0, category: "", q: "", sort: "trust",
  });
  const [data, setData] = useState({ total: 0, providers: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { ...filters };
    Object.keys(params).forEach((k) => { if (params[k] === "" || params[k] === 0) delete params[k]; });
    api.get("/providers/discover", { params, paramsSerializer: (p) => new URLSearchParams(p).toString() })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters]);

  const setF = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const reset = () => setFilters({ tier: "", tradition: "", minEndorsements: 0, category: "", q: "", sort: "trust" });

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <div className="label-mystic">Find your practitioner</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-2">Discover</h1>
          <p className="text-foreground/70 mt-2 max-w-2xl">
            Filter by verified tier, lineage, and community endorsements. The trust ladder is built from KYC, phone, business documents, and peer endorsements from Verified Initiates &amp; Elders.
          </p>
        </div>

        <AIMatchSearch />

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
          {/* Filter sidebar */}
          <aside className="space-y-5" data-testid="discover-filters">
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-2 text-secondary/85">
                <Filter size={14} strokeWidth={1.5} />
                <span className="label-mystic">Filters</span>
              </div>
              <button
                onClick={reset}
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] text-foreground/55 hover:text-secondary"
                data-testid="discover-reset"
              >
                <X size={11} /> Reset
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
              <input
                value={filters.q}
                onChange={(e) => setF({ q: e.target.value })}
                placeholder="Search by name"
                className="w-full bg-input border border-secondary/20 pl-9 pr-3 py-2.5 rounded-sm focus:outline-none focus:border-secondary text-sm"
                data-testid="discover-search"
              />
            </div>

            {/* Tier */}
            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-[0.16em] text-foreground/55">Verified Tier</div>
              <div className="flex flex-wrap gap-1.5">
                {TIER_FILTERS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setF({ tier: t.id })}
                    className={`px-2.5 py-1 rounded-full border text-[11px] uppercase tracking-wider ${filters.tier === t.id ? "border-secondary text-secondary bg-secondary/10" : "border-secondary/25 text-foreground/65 hover:border-secondary/50"}`}
                    data-testid={`tier-filter-${t.id || "any"}`}
                  >{t.label}</button>
                ))}
              </div>
            </div>

            {/* Tradition */}
            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-[0.16em] text-foreground/55">Tradition</div>
              <input
                value={filters.tradition}
                onChange={(e) => setF({ tradition: e.target.value })}
                placeholder="Ifá · Vodou · Quimbanda..."
                className="w-full bg-input border border-secondary/20 px-3 py-2 rounded-sm focus:outline-none focus:border-secondary text-sm"
                data-testid="tradition-filter"
              />
            </div>

            {/* Min endorsements */}
            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-[0.16em] text-foreground/55">
                Min endorsements: <span className="text-secondary">{filters.minEndorsements}</span>
              </div>
              <input
                type="range" min={0} max={10} step={1}
                value={filters.minEndorsements}
                onChange={(e) => setF({ minEndorsements: parseInt(e.target.value) })}
                className="w-full accent-secondary"
                data-testid="min-endorsements-filter"
              />
            </div>

            {/* Category */}
            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-[0.16em] text-foreground/55">Category</div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setF({ category: "" })}
                  className={`px-2.5 py-1 rounded-full border text-[11px] uppercase tracking-wider ${!filters.category ? "border-secondary text-secondary bg-secondary/10" : "border-secondary/25 text-foreground/65"}`}
                  data-testid="category-filter-any"
                >Any</button>
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setF({ category: filters.category === c ? "" : c })}
                    className={`px-2.5 py-1 rounded-full border text-[11px] uppercase tracking-wider ${filters.category === c ? "border-secondary text-secondary bg-secondary/10" : "border-secondary/25 text-foreground/65 hover:border-secondary/50"}`}
                    data-testid={`category-filter-${c.toLowerCase().replace(/ /g, "-")}`}
                  >{c}</button>
                ))}
              </div>
            </div>

            {/* Sort */}
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-foreground/55">
                <ArrowUpDown size={11} /> Sort
              </div>
              <select
                value={filters.sort}
                onChange={(e) => setF({ sort: e.target.value })}
                className="w-full bg-input border border-secondary/20 px-3 py-2 rounded-sm focus:outline-none focus:border-secondary text-sm"
                data-testid="sort-select"
              >
                {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          </aside>

          {/* Results */}
          <section className="space-y-3" data-testid="discover-results">
            <div className="flex items-center justify-between">
              <div className="label-mystic" data-testid="discover-count">
                {loading ? "Searching…" : `${data.total} practitioner${data.total === 1 ? "" : "s"}`}
              </div>
            </div>

            {!loading && data.providers.length === 0 && (
              <div className="parchment p-12 text-center text-foreground/55 italic font-serif text-xl" data-testid="discover-empty">
                No practitioners match these filters. Try widening your search.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.providers.map((p) => (
                <ProviderCard key={p.userId} p={p} q={filters.q} />
              ))}
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}

function ProviderCard({ p, q }) {
  const name = displayName(p);
  // Pass referrer + search term so backend view-tracking can populate
  // Pro Insights' search-term + source-breakdown analytics.
  const params = new URLSearchParams({ from: "discover" });
  if (q) params.set("q", q);
  const to = `/profile/${p.userId}?${params.toString()}`;
  return (
    <Link
      to={to}
      className="parchment p-5 hover:border-secondary/40 transition-colors block group"
      data-testid={`discover-card-${p.userId}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-full overflow-hidden bg-card border border-secondary/20 flex-shrink-0">
          {p.avatarUrl || p.user?.avatarUrl ? (
            <img src={p.avatarUrl || p.user?.avatarUrl} alt="" onError={hideOnError} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-serif text-2xl text-secondary">
              {name?.[0]?.toUpperCase() || "?"}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h3 className="font-serif text-xl truncate group-hover:text-secondary transition-colors">{name}</h3>
            <VerifiedBadge size={14} show={p.verified} />
            <ElderBadge size={14} show={p.elder} index={p.elderIndex} />
          </div>
          {p.verifiedTier && p.verifiedTier !== "none" && (
            <div className="mt-1 inline-flex items-center gap-2">
              <VerifiedTierBadge tier={p.verifiedTier} size={11} />
              {p.trustScore != null && <TrustScoreBadge score={p.trustScore} providerId={p.userId} compact />}
            </div>
          )}
          {p.tradition && (
            <div className="text-[11px] text-foreground/60 mt-1 truncate">
              <Sparkles size={10} className="inline -mt-0.5 mr-1 text-violet-300/80" />
              {p.tradition}
              {p.yearsInitiated != null && <span className="text-foreground/40"> · {p.yearsInitiated}y initiated</span>}
            </div>
          )}
          <div className="flex items-center gap-3 mt-2 text-[11px] text-foreground/55 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <StarRating value={p.averageRating || 0} testid={`card-rating-${p.userId}`} />
              <span>{(p.averageRating || 0).toFixed(1)} · {p.totalReviews || 0}</span>
            </span>
            {p.endorsementCount > 0 && (
              <span className="inline-flex items-center gap-1 text-violet-300/80">
                <Sparkles size={10} /> {p.endorsementCount} endorsement{p.endorsementCount === 1 ? "" : "s"}
              </span>
            )}
            {p.category && <span className="text-foreground/50">· {p.category}</span>}
          </div>
        </div>
      </div>
    </Link>
  );
}
